// Sing in Solfège state machine (issue #3722): a pure reducer over one session of four hymn
// phrases, driven by microphone frames and the page's actions. It follows Interval Training's
// session (interval_session.ts) in everything but the one thing the module changes: the singer is
// not made to get each note right before moving on. The phrase runs in tempo from a one-bar
// count-in and never waits, and each note is graded once its window has passed, by the share of
// it the voice spent on or near the note (common/src/sing_in_solfege.ts). No React, no DOM, no
// timers, no audio. The page holds the state in useReducer, dispatches each mic frame, plays each
// sound request once and dispatches `soundStarted` when the singer heard it start, polls the audio
// clock while solfegeClockRunning says so and dispatches `tick`, and renders from the selectors
// below. Tests drive it the same way with synthetic frames.
//
// Every time here is absolute ms on the audio clock: frames carry it (CaptureFrame.timeMs, the end
// of the analysed window), `soundStarted` carries the moment the singer heard the sound start, and
// `tick` the page's reading of the clock. `clockMs` is the latest of them the machine has seen.
//
// Phases, for each phrase:
//
//   listen ──sing──▶ sing (count-in, then the take) ──the take's tail──▶ result ──next──▶ listen (the next phrase)
//     ▲                 │  ▲ startOver                                    │  │
//     └──interrupted────┘  └──────────────Sing again────────────────────────┘  └──next, after the fourth──▶ summary
//
//   listen, sing or result ──micFailed──▶ failed ──retry──▶ the result as it was, or listen
//
// Sounds. One request at a time (`sound`), each replacing the one before, played once per id by the
// page and reported with `soundStarted`; a report for an id since replaced is ignored, and a request
// that goes away tells the page to stop it. solfegeSoundPlan turns a request into what the page
// schedules:
//   - 'phrase': the phrase on the piano in its tempo, the listen screen's sound, asked for on
//     entering listen and again by "Play again", and on the result by "Hear guide".
//   - 'take': the count-in and, in sing-along, the guide. A click on each of the bar's beats, the
//     phrase's first note for the first beat (the starting pitch), and in sing-along the phrase
//     itself, soft, from the end of the count-in. In guide-first nothing sounds after the count-in.
//     The take's start - when the first note should begin - is the reported start plus one bar, so
//     everything the take is timed by follows what the singer heard.
//   - 'recording': "Hear mine", the take played back from the page's memory. The machine knows only
//     which part of the take it is (the page's `clip`, or the take's whole span) and when it started,
//     so the screen can say "Stop" and move a playhead; the page owns the audio.
//
// Listen. The phrase plays by itself on entering, and the notes light in time against the sound's
// reported start. When it has finished, listen waits for "Sing it with me" (sing-along) or "Sing
// it" (guide-first); either cuts a phrase still playing (the request is replaced) and starts the
// count-in. The microphone is not read here: the listen screen has no meter, and a frame that
// changes nothing on screen should not render it.
//
// The count-in. Frames move the meter, and once the starting pitch has finished sounding they are
// read too, so a singer who hums the first note before it counts sees where they are (#3722, round
// 2). A frame is read when what it heard was sung - its time less voiceLagMs - at least
// guideTailMs (the tail Interval Training waits after a guide: the piano's release, the canceller
// settling) after the starting pitch ended, so the dot never shows the piano itself. Nor does it show
// the count-in's own clicks, which a phone's mic hears from its speaker: a frame that heard a click
// (solfegeClickLeadMs before it to solfegeClickMaskMs after it, on the voice's clock) moves the meter
// only, and in sing-along so does a frame that may have heard the guide's first note begin, at the
// take's start. A frame that is read is read with the scorer against the first note and moves the
// count-in's own register (countInRegister), which folds the count-in's dot and nothing else: the
// take starts with no register, so an octave a click or a hum suggested before the first note never
// folds the take's readings. Nor is a count-in frame tallied, graded, drawn in the trace or said to
// have heard a note. The dot it gives sits at the start of the first note (intoMs 0) at the singer's
// pitch, with the hint; with no voice there is no dot before the take. The take's first frame starts
// the scorer afresh, so no count-in reading is smoothed into it.
//
// Ready · Set · Go! (#3722, round 2). Over the count-in's last two beats and the take's first, the
// words Rhythm Echo pulses (learn_cue.ts) pulse large under the staff's status line: "Ready" on the
// beat two before the take starts, "Set" on the beat before it, "Go!" on the take's first beat, the
// first note's, as the singer comes in. The count-in's dots and number count every beat as before; the words are added,
// not swapped for them. solfegeCueNow reads them off the clock against the take's start, so any
// instant renders the same, and the clock runs throughout (solfegeClockRunning: the count-in and the
// take), "Go!"'s fall into the take's first beat included. A new count-in has no start until its
// sound reports one, so Start over and Sing again show no word until their own "Ready".
//
// The take. From the take's start each frame is
// read with the shared note scorer (note_scorer.ts: readings, smoothing, its octave rule, the hint
// and the dot's colour) against the note it belongs to; its hold, lock and too-noisy are not used,
// because nothing here waits on a note and every take ends by itself. A frame belongs to the note
// the singer was on by the voice's own clock: its time less voiceLagMs, the mic's input latency
// plus the analysis window, which grading shifts every window by too. The trace, the per-note
// tallies and the dot all read the frame against that note, except across the switch below.
//
// The estimator's aim. The known-target estimator reads a frame relative to the note it was aimed
// at when it analysed the frame, frames reach it up to ~65 ms after their time (the worklet's
// backlog), and a new aim reaches it a render after the page dispatches it. So around each switch
// there is a stretch of frames that may have been read against either note. The aim switches - and
// the machine starts reading frames against the new note - aimDelayMs after the note's lagged start,
// which puts that stretch after the end of the previous note's window and before the start of the
// next one's: a window starts at least min(settleMaxMs, settleShare × minGradedMs) = 90 ms after the
// lagged start, and the stretch runs from about 50 ms before the switch to about 15 ms after it.
// The few trace samples inside it may be read against the wrong note; the trace's own smoothing
// (step 3 of the plan, a five-sample median) is what absorbs them. YIN's readings, which are
// absolute, are right throughout.
//
// Grading. Over each graded note's window (phraseGradingWindows on the take's start) the time each
// confident reading covers - the frame's own span, clipped to the window - is added to the note's
// time on pitch (within toleranceCents) and time close (within closeCents); silence, noise and weak
// readings add to neither, as a weak reading never counts toward the scorer's hold. Confident
// readings in the window also keep their cents, octave-folded, for the median the result sentence
// reads, and say the note was heard. A note's colour is set the moment its window closes: on the
// first frame at or past its end - once that frame's own part inside the window is counted - or on
// a tick frameLateMs past it, which leaves frames still in the worklet's backlog time to arrive;
// whatever is still open when the take ends is closed then. A passing note (phraseGradedNotes) is
// never graded, coloured or counted.
//
// Octaves. With octaveCounts, cents are folded into the singer's register before anything reads
// them (solfegeFoldCents in common/src/sing_in_solfege.ts): a reading near a whole number of
// octaves from the note sets the take's register, and every other reading is folded by the
// register last set, so a man singing a soprano melody an octave down draws his trace through the
// notes and is graded on them, and a voice more than a tritone off - the singer still on do as the
// phrase leaps to sol - is flat or sharp the way it really is, for the hint, the dot, the sentence
// and the line alike. The scorer folds the octave errors the detectors make onto the target and
// flags them; the machine unfolds those first, so the register sees where the voice really is. The
// register moves on each frame's own reading, before the scorer smooths it (registerAfter).
//
// The trace keeps one sample about every traceSampleMs of voice, readings weak or not, in a linked
// stack so a frame adds one node rather than copying the take so far; solfegeTraceSamples flattens
// it once per stack. Each sample says whether its reading was weak, so the line can leave out a
// stretch with no confident reading in it - a breath, a sound in the room - while a weak reading
// inside real singing still draws (solfege_trace.ts). It stays with the take on the result and is
// dropped with it.
//
// Interruptions. `interrupted` (the page hidden, or the mic dropped) during the count-in or the take
// abandons the take and returns to listen, idle: no exercise pauses a running timeline yet (#3747
// only mutes the bus), so nothing tries to resume mid-phrase. Anywhere else it stops any sound and
// changes nothing more. `micFailed` in listen, sing or result goes to failed, dropping a take in
// progress; retry returns to the result as it was, or to listen with the phrase played again (from
// a failure in sing too: the singer has just tapped Try again, hears the phrase, and starts when
// ready).
//
// Result and summary. The result shows the take just sung; "Sing again" goes straight to a new
// count-in on the same phrase and its result replaces this one. "Next phrase" files the last take's
// colours and opens the next phrase's listen, or the summary after the fourth, which scores the
// session over the filed colours with step 1's helpers.
//
// The result's first moments. The take ends on the clock, so the sing screen's "Start over" turns
// into the result's buttons under a finger that may already be on its way to it. For
// solfegeResultGuardMs after the result is reached (on the audio clock) "Next phrase", "Sing again",
// "Hear mine" and "Hear guide" are ignored and the screen shows them disabled, and the clock keeps running
// through that span (solfegeClockRunning), so the guard lifts by the clock alone, with no frame
// needed. It also gives the page's capture time to catch up before the take is cut from it.
//
// Hear mine. The page plays what it kept of the take, which may be a little less than the take's
// span (the rolling capture did not reach that far back); it passes that part with `hearMine` as a
// span on the take's scale, so the playhead starts where the audio does and "Stop" lasts exactly as
// long as the sound. Without one the whole take is assumed.
//
// Hear guide. A singer who sang on their own can hear the phrase again from the result: the
// listen screen's 'phrase' sound, at full volume and in tempo, which the page plays as it plays it
// there. While it plays the result says "Stop", a playhead crosses the staff with it (the phrase's
// sound starts where the take's first note does, so its time is the take's), and it ends by itself;
// the result's colours and the line do not change. One sound at a time: Hear guide and Hear mine
// each replace the other, and Sing again and Next phrase replace either.

import { SolfegeSyllable } from '../../../../../common/src/interval_training.ts';
import { learnListeningTunables } from '../../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../../common/src/model.ts';
import { ResultColor } from '../../../../../common/src/rhythm_echo.ts';
import {
  phraseBeatMs,
  phraseCountInMs,
  phraseGradedNotes,
  phraseGradingWindows,
  phraseLengthMs,
  phraseNoteSpans,
  phraseSemitones,
  phraseStart,
  phraseSyllables,
  phraseTakeEndMs,
  solfegeFoldCents,
  SolfegeLevel,
  SolfegeLevelParams,
  solfegeLevelParams,
  solfegeLongestGreenRun,
  solfegeNoteColor,
  SolfegeNoteColors,
  solfegeNoteCountText,
  solfegeNoteMidi,
  SolfegePhrase,
  solfegePhraseColor,
  solfegeSessionScore,
  SolfegeSpan,
} from '../../../../../common/src/sing_in_solfege.ts';
import { frameDelta } from '../audio/frame_reading.ts';
import { CaptureFrame, MicFailure } from '../audio/learn_mic_capture.ts';
import { VisibleCue, visibleCue } from '../learn_cue.ts';
import {
  freshNoteScore,
  hearFrame,
  NoteScore,
  NoteScoringParams,
  PitchReading,
  readingDot,
  readingHint,
  scoreFrame,
} from '../pitch/note_scorer.ts';

// How long after a note's lagged start the estimator's aim, and the note frames are read against,
// switch to it (see The estimator's aim in the header)
export const solfegeAimDelayMs = 60;

// A tick closes a note's window only this long after its end, so frames still in the worklet's
// backlog (up to ~65 ms) and the render that delivers them are counted first; frames close it at once
export const solfegeFrameLateMs = 100;

// About one trace sample per this much voice: ~400 points for a take, plenty for a smooth line
export const solfegeTraceSampleMs = 30;

// For this long after the take ends on the clock the result's buttons are not taken: a press that
// began on "Start over" as the take ended must not land on "Next phrase" (see The result's first
// moments in the header). Long enough for a tap already on its way; short enough not to be felt.
export const solfegeResultGuardMs = 600;

// A count-in frame is not read from this long before a click (on the voice's clock: the frame's time
// less voiceLagMs) to solfegeClickMaskMs after it, so a mic that hears the phone's own speaker never
// shows the click as a dot or a hint (#3722, round 2 review). The lead is one analysis window (2048
// samples, ~43-46 ms): a frame whose window opened just before the click hears it at its end. The
// mask after it covers the click's decay and the room's first reflections with some to spare for a
// mic whose lag is longer than voiceLagMs, and is short enough that a singer humming between clicks
// keeps their dot (the scorer holds a reading through the gap).
export const solfegeClickLeadMs = 50;
export const solfegeClickMaskMs = 120;

// ---- State -----------------------------------------------------------------------------------

// Sing-along: the guide plays softly under the take. Guide-first: the guide plays in listen and is
// quiet during the take. One stored choice per device, this module's own, off until the singer turns
// it on (sing_in_solfege_page.tsx).
export type SolfegeGuideMode = 'singAlong' | 'guideFirst';

// A phrase of the session with its own do, placed in the singer's range by pickPhraseHomeNote
export interface SolfegeSessionPhrase {
  phrase: SolfegePhrase;
  homeMidi: number;
}

export type SolfegePhase = 'listen' | 'sing' | 'result' | 'summary' | 'failed';

// The phases a failure can strike in
export type SolfegeResumePhase = 'listen' | 'sing' | 'result';

export type SolfegeSoundKind = 'phrase' | 'take' | 'recording';

export interface SolfegeSoundRequest {
  kind: SolfegeSoundKind;
  id: number; // the page plays each id once
  atMs?: number; // when the singer heard it start, from soundStarted
  endsMs?: number; // when it ends, set with atMs
  clip?: SolfegeSpan; // a 'recording': the part of the take the page kept, on the take's scale
}

// One piano note of a sound, from the sound's start. `soft` notes are the sing-along guide under
// the take, played at a reduced gain. A note of the phrase carries its index in the phrase; the
// count-in's starting pitch carries none.
export interface SolfegeSoundNote {
  midi: number;
  offsetMs: number;
  durationMs: number;
  soft: boolean;
  noteIndex?: number;
}

// What the page schedules for a request: piano notes, clicks (from the sound's start), and how long
// the whole sound lasts. A 'recording' has neither notes nor clicks: the page plays the take.
export interface SolfegeSoundPlan {
  kind: SolfegeSoundKind;
  notes: SolfegeSoundNote[];
  clickOffsetsMs: number[];
  lengthMs: number;
}

// One point of the singer's line. `intoMs` is when the singer sang it, from the take's start (the
// frame's time less voiceLagMs), on the same scale as phraseNoteSpans; `midi` the voice as a
// fractional MIDI number, octave-folded toward `noteIndex`'s note when octaves count; `cents` its
// signed distance from that note, which colours the line. `weak` is the reading's own flag: too
// quiet or unclear to count toward a grade, as grading leaves it out.
export interface SolfegeTraceSample {
  intoMs: number;
  midi: number;
  cents: number;
  noteIndex: number;
  weak: boolean;
}

// A graded note once its window has closed. `medianCents` is the median folded deviation of the
// confident readings in the window; undefined, with `heard` false, when there were none.
export interface SolfegeNoteGrade {
  color: ResultColor;
  onShare: number;
  nearShare: number;
  heard: boolean;
  medianCents?: number;
}

// An immutable stack: pushing shares everything below, so a frame adds one node
export interface SolfegeStack<T> {
  readonly top: T;
  readonly rest?: SolfegeStack<T>;
  readonly size: number;
}

// What the window of note `index` has gathered so far
interface SolfegeTally {
  index: number;
  onMs: number;
  nearMs: number;
  deviations?: SolfegeStack<number>;
}

export interface SolfegeTake {
  startMs?: number; // when the first note starts; undefined until the count-in reports its start
  endedAtMs?: number; // the clock when the take ended and the result was reached
  register?: number; // the octaves from the notes the singer is singing in (solfegeFoldCents); 0 when unset
  // The count-in's own register, which folds the count-in's dot alone: the take never starts from it
  // (see The count-in in the header)
  countInRegister?: number;
  // The note the latest in-phrase frame was read against; undefined in the count-in, where a frame
  // that is read at all is read against the first note
  readingIndex?: number;
  tally?: SolfegeTally; // the open window's tally
  grades: (SolfegeNoteGrade | undefined)[]; // one per closed window, in order; undefined for a passing note
  trace?: SolfegeStack<SolfegeTraceSample>;
  sampledAtMs?: number; // the frame time of the latest trace sample
}

export interface SolfegeSessionState {
  phase: SolfegePhase;
  level: SolfegeLevel;
  guide: SolfegeGuideMode;
  phrases: SolfegeSessionPhrase[];
  finished: SolfegeNoteColors[]; // the last take's colours of each phrase behind the singer; its length is the phrase index
  take?: SolfegeTake; // in sing the take under way (its count-in included), in result the take just sung
  score: NoteScore; // the scorer's state: the meter's level, and during the take the latest reading
  sound?: SolfegeSoundRequest;
  clockMs?: number;
  failure?: MicFailure;
  failedFrom?: SolfegeResumePhase;
  nextRequestId: number;
}

// ---- Actions ---------------------------------------------------------------------------------

export type SolfegeSessionAction =
  | { type: 'frame'; frame: CaptureFrame } // one analysed frame from the mic
  | { type: 'tick'; nowMs: number } // the audio clock, while solfegeClockRunning
  | { type: 'soundStarted'; requestId: number; atMs: number } // the singer heard sound `requestId` start at `atMs`
  | { type: 'hearAgain' } // "Play again" on the listen screen
  | { type: 'sing' } // "Sing it with me" / "Sing it" on the listen screen
  | { type: 'startOver' } // "Start over" on the sing screen
  | { type: 'interrupted' } // the page was hidden or the mic dropped
  | { type: 'hearMine'; clip?: SolfegeSpan } // "Hear mine" / "Stop" on the result screen, with what the page kept
  | { type: 'hearGuide' } // "Hear guide" / "Stop" on the result screen
  | { type: 'stopHearing' } // stop the playback
  | { type: 'singAgain' } // "Sing again" on the result screen
  | { type: 'next' } // "Next phrase" / "See your score" on the result screen
  | { type: 'micFailed'; failure: MicFailure }
  | { type: 'retry' }; // from failed: the mic is open again

// A session at its beginning: the first phrase's listen with its sound asked for. The page builds
// one per run, so the request ids start again from 1 with it.
export function initialSolfegeSessionState({
  level,
  phrases,
  guide,
}: {
  level: SolfegeLevel;
  phrases: SolfegeSessionPhrase[];
  guide: SolfegeGuideMode;
}): SolfegeSessionState {
  return {
    phase: 'listen',
    level,
    guide,
    phrases,
    finished: [],
    score: freshNoteScore(),
    sound: { id: 1, kind: 'phrase' },
    nextRequestId: 2,
  };
}

export function reduceSolfegeSession(state: SolfegeSessionState, action: SolfegeSessionAction): SolfegeSessionState {
  switch (action.type) {
    case 'frame':
      return frame(state, action.frame);
    case 'tick':
      return tick(state, action.nowMs);
    case 'soundStarted':
      return soundStarted(state, action.requestId, action.atMs);
    case 'hearAgain':
      return state.phase === 'listen' ? requestSound(state, 'phrase') : state;
    case 'sing':
      return state.phase === 'listen' ? toCountIn(state) : state;
    case 'startOver':
      return state.phase === 'sing' ? toCountIn(state) : state;
    case 'interrupted':
      return interrupted(state);
    case 'hearMine':
      return hearMine(state, action.clip);
    case 'hearGuide':
      return hearGuide(state);
    case 'stopHearing':
      return isSolfegeHearingMine(state) ? { ...state, sound: undefined } : state;
    case 'singAgain':
      return state.phase === 'result' && !isSolfegeResultSettling(state) ? toCountIn(state) : state;
    case 'next':
      return next(state);
    case 'micFailed':
      return micFailed(state, action.failure);
    case 'retry':
      return retry(state);
  }
}

// ---- Timing ----------------------------------------------------------------------------------

// A phrase's timing at a level, worked out once: frames ask for it ~94 times a second
interface PhraseTiming {
  beatMs: number;
  countInMs: number;
  lengthMs: number; // the last note's end
  takeEndMs: number; // the take's end, the tail included
  spans: SolfegeSpan[];
  windows: SolfegeSpan[];
  graded: boolean[];
}

const timings = new WeakMap<SolfegeLevelParams, WeakMap<SolfegePhrase, PhraseTiming>>();

function timingOf(phrase: SolfegePhrase, params: SolfegeLevelParams): PhraseTiming {
  let byPhrase = timings.get(params);
  if (!byPhrase) {
    byPhrase = new WeakMap();
    timings.set(params, byPhrase);
  }
  const known = byPhrase.get(phrase);
  if (known) {
    return known;
  }
  const timing: PhraseTiming = {
    beatMs: phraseBeatMs(phrase),
    countInMs: phraseCountInMs(phrase),
    lengthMs: phraseLengthMs(phrase),
    takeEndMs: phraseTakeEndMs(phrase, params),
    spans: phraseNoteSpans(phrase),
    windows: phraseGradingWindows(phrase, params),
    graded: phraseGradedNotes(phrase, params),
  };
  byPhrase.set(phrase, timing);
  return timing;
}

// The note sounding `intoMs` after the take's start, clamped to the phrase's first and last notes
function noteIndexAt(spans: SolfegeSpan[], intoMs: number): number {
  let index = 0;
  while (index + 1 < spans.length && spans[index + 1].startMs <= intoMs) {
    index += 1;
  }
  return index;
}

function paramsOf(state: SolfegeSessionState): SolfegeLevelParams {
  return solfegeLevelParams[state.level];
}

function currentEntry(state: SolfegeSessionState): SolfegeSessionPhrase {
  return state.phrases[Math.min(state.finished.length, state.phrases.length - 1)];
}

function currentTiming(state: SolfegeSessionState): PhraseTiming {
  return timingOf(currentEntry(state).phrase, paramsOf(state));
}

// One note's pitch, without building the phrase's (frames ask ~94 times a second)
function noteMidi(state: SolfegeSessionState, index: number): number {
  const { phrase, homeMidi } = currentEntry(state);
  return solfegeNoteMidi(homeMidi, phrase.notes[index]);
}

// ---- Transitions -----------------------------------------------------------------------------

function requestSound(state: SolfegeSessionState, kind: SolfegeSoundKind): SolfegeSessionState {
  return { ...state, sound: { kind, id: state.nextRequestId }, nextRequestId: state.nextRequestId + 1 };
}

// The latest time the machine has seen; the state itself when the reading is not new
function withClock(state: SolfegeSessionState, nowMs: number): SolfegeSessionState {
  if (state.clockMs !== undefined && state.clockMs >= nowMs) {
    return state;
  }
  return { ...state, clockMs: nowMs };
}

function soundStarted(state: SolfegeSessionState, requestId: number, atMs: number): SolfegeSessionState {
  const sound = state.sound;
  if (!sound || sound.id !== requestId || sound.atMs !== undefined) {
    return state; // a request since replaced, or one already reported
  }
  const started = { ...sound, atMs, endsMs: atMs + soundPlanFor(state, sound).lengthMs };
  if (sound.kind !== 'take' || !state.take) {
    return { ...state, sound: started };
  }
  // The take is timed from what the singer heard: one bar after the count-in started
  return { ...state, sound: started, take: { ...state.take, startMs: atMs + currentTiming(state).countInMs } };
}

// Into listen for the current phrase, auto-playing it or idle, with no take
function toListen(state: SolfegeSessionState, autoPlay: boolean): SolfegeSessionState {
  const listening: SolfegeSessionState = {
    ...state,
    phase: 'listen',
    take: undefined,
    score: freshNoteScore(state.score),
    failure: undefined,
    failedFrom: undefined,
  };
  return autoPlay ? requestSound(listening, 'phrase') : { ...listening, sound: undefined };
}

// A new count-in and a fresh take on the current phrase: "Sing it", "Start over" and "Sing again".
// Whatever was playing is replaced, and a take already sung is dropped with its grades.
function toCountIn(state: SolfegeSessionState): SolfegeSessionState {
  return {
    ...requestSound(state, 'take'),
    phase: 'sing',
    take: { grades: [] },
    score: freshNoteScore(state.score),
  };
}

function interrupted(state: SolfegeSessionState): SolfegeSessionState {
  if (state.phase === 'sing') {
    return toListen(state, false);
  }
  return isSoundLive(state) ? { ...state, sound: undefined } : state;
}

// A sound asked for and not yet heard, or still playing: one that has ended needs no stopping
function isSoundLive(state: SolfegeSessionState): boolean {
  return state.sound !== undefined && (state.sound.endsMs === undefined || isSoundPlaying(state));
}

// "Hear mine" plays the take back - the part the page kept, when it says - and stops it when it is
// playing ("Stop")
function hearMine(state: SolfegeSessionState, clip: SolfegeSpan | undefined): SolfegeSessionState {
  if (state.phase !== 'result' || state.take?.startMs === undefined || isSolfegeResultSettling(state)) {
    return state;
  }
  if (isSolfegeHearingMine(state)) {
    return { ...state, sound: undefined };
  }
  const requested = requestSound(state, 'recording');
  return clip ? { ...requested, sound: { ...requested.sound!, clip } } : requested;
}

// "Hear guide" plays the phrase, as the listen screen does, and stops it when it is playing ("Stop")
function hearGuide(state: SolfegeSessionState): SolfegeSessionState {
  if (state.phase !== 'result' || isSolfegeResultSettling(state)) {
    return state;
  }
  if (isSolfegeHearingGuide(state)) {
    return { ...state, sound: undefined };
  }
  return requestSound(state, 'phrase');
}

function next(state: SolfegeSessionState): SolfegeSessionState {
  if (state.phase !== 'result' || isSolfegeResultSettling(state)) {
    return state;
  }
  const finished = [...state.finished, solfegeResultColors(state)];
  if (finished.length >= state.phrases.length) {
    return { ...state, phase: 'summary', finished, take: undefined, sound: undefined };
  }
  return toListen({ ...state, finished }, true);
}

// A second failure before a retry keeps the phase the first struck in. A take in progress is lost;
// a result keeps its take.
function micFailed(state: SolfegeSessionState, failure: MicFailure): SolfegeSessionState {
  if (state.phase === 'summary') {
    return state;
  }
  const failedFrom = state.phase === 'failed' ? state.failedFrom : state.phase;
  const take = failedFrom === 'result' ? state.take : undefined;
  return { ...state, phase: 'failed', failure, failedFrom, take, sound: undefined };
}

function retry(state: SolfegeSessionState): SolfegeSessionState {
  if (state.phase !== 'failed') {
    return state;
  }
  if (state.failedFrom === 'result') {
    return { ...state, phase: 'result', failure: undefined, failedFrom: undefined };
  }
  return toListen(state, true);
}

// The clock moves while a sound plays and through the take; a tick that tells the machine nothing
// returns the state itself, so the page's caller can bail out of the render
function tick(state: SolfegeSessionState, nowMs: number): SolfegeSessionState {
  if (state.phase === 'sing') {
    if (state.take?.startMs === undefined) {
      return state; // a count-in asked for and not yet heard has nothing to time
    }
    return advanceTake(withClock(state, nowMs), nowMs - solfegeFrameLateMs);
  }
  if (state.phase === 'listen' || state.phase === 'result') {
    return isSoundPlaying(state) || isSolfegeResultSettling(state) ? withClock(state, nowMs) : state;
  }
  return state;
}

// Windows that have closed by `closeUpToMs` get their grades, and the take ends - every window
// still open closed, the phase result - once the clock passes its tail
function advanceTake(state: SolfegeSessionState, closeUpToMs: number): SolfegeSessionState {
  const take = state.take;
  if (state.phase !== 'sing' || take?.startMs === undefined) {
    return state;
  }
  const timing = currentTiming(state);
  const params = paramsOf(state);
  if ((state.clockMs ?? -Infinity) >= take.startMs + timing.takeEndMs) {
    const ended = { ...closeWindows(take, timing, params, Infinity), endedAtMs: state.clockMs };
    return { ...state, phase: 'result', take: ended };
  }
  const closed = closeWindows(take, timing, params, closeUpToMs);
  return closed === take ? state : { ...state, take: closed };
}

// The take with every window ending by `upToMs` graded, in order, up to (not including) note
// `beforeIndex`; the take itself when none is
function closeWindows(
  take: SolfegeTake,
  timing: PhraseTiming,
  params: SolfegeLevelParams,
  upToMs: number,
  beforeIndex = Infinity,
): SolfegeTake {
  const startMs = take.startMs ?? 0;
  const limit = Math.min(timing.windows.length, beforeIndex);
  let grades = take.grades;
  while (grades.length < limit && startMs + timing.windows[grades.length].endMs <= upToMs) {
    grades = [...grades, gradeOf(take.tally, grades.length, timing, params)];
  }
  return grades === take.grades ? take : { ...take, grades };
}

function gradeOf(
  tally: SolfegeTally | undefined,
  index: number,
  timing: PhraseTiming,
  params: SolfegeLevelParams,
): SolfegeNoteGrade | undefined {
  if (!timing.graded[index]) {
    return undefined; // a passing note
  }
  const window = timing.windows[index];
  const lengthMs = window.endMs - window.startMs;
  const own = tally?.index === index ? tally : undefined;
  const onShare = (own?.onMs ?? 0) / lengthMs;
  const nearShare = (own?.nearMs ?? 0) / lengthMs;
  const deviations = stackToArray(own?.deviations);
  return {
    color: solfegeNoteColor(onShare, nearShare, params),
    onShare,
    nearShare,
    heard: deviations.length > 0,
    medianCents: deviations.length > 0 ? median(deviations) : undefined,
  };
}

// ---- Frames ----------------------------------------------------------------------------------

function frame(state: SolfegeSessionState, captured: CaptureFrame): SolfegeSessionState {
  if (state.phase !== 'sing' || !state.take) {
    return state; // only the sing screen reads the microphone
  }
  const clocked = withClock(state, captured.timeMs);
  return advanceTake(readFrame(clocked, captured), captured.timeMs);
}

// The scorer's cents as the voice really is: an octave the scorer's own rule folded onto the target,
// and flagged, put back, so the register sees it (see Octaves in the header)
function unfoldedCents(reading: PitchReading): number {
  return reading.cents + (reading.octave === 'below' ? -1200 : reading.octave === 'above' ? 1200 : 0);
}

// The scorer's reading of the frame, with its octave rule; a hold that can never complete, since
// nothing here waits on a note
function scoringParams(params: SolfegeLevelParams): NoteScoringParams {
  return { toleranceCents: params.toleranceCents, octaveCounts: params.octaveCounts, holdSeconds: Infinity };
}

// The note a frame at `timeMs` is read against: the note the estimator was aimed at for it
function readingIndexAt(timing: PhraseTiming, params: SolfegeLevelParams, startMs: number, timeMs: number): number {
  return noteIndexAt(timing.spans, timeMs - startMs - params.voiceLagMs - solfegeAimDelayMs);
}

function readFrame(state: SolfegeSessionState, captured: CaptureFrame): SolfegeSessionState {
  const take = state.take;
  const startMs = take?.startMs;
  if (!take || startMs === undefined) {
    return { ...state, score: hearFrame(state.score, captured) }; // the count-in: the meter moves
  }
  const timing = currentTiming(state);
  const params = paramsOf(state);
  const timeMs = captured.timeMs;
  const intoMs = timeMs - startMs - params.voiceLagMs; // when the singer sang what this frame heard
  if (intoMs < 0) {
    return countInFrame(state, captured, intoMs, timing);
  }
  // The frame covers the voice from intoMs less its span to intoMs: one that ends past the last note
  // may still have part of it inside the last window
  const spanFromMs = intoMs - frameDelta(state.score.lastFrameMs, timeMs);
  if (spanFromMs >= timing.lengthMs) {
    const closed = closeWindows(take, timing, params, timeMs);
    return { ...state, take: closed, score: hearFrame(state.score, captured) };
  }
  const index = readingIndexAt(timing, params, startMs, timeMs);
  // Windows of the notes before this frame's close on the tally they gathered, before it opens its own
  const opened = closeWindows(take, timing, params, timeMs, index);
  const base = index === opened.readingIndex ? state.score : freshNoteScore(state.score);
  const deltaMs = frameDelta(base.lastFrameMs, timeMs);
  const { score } = scoreFrame(base, captured, noteMidi(state, index), scoringParams(params));
  const reading = score.readingAtMs === timeMs ? score.reading : undefined; // read on this frame
  const register = registerAfter(score, timeMs, opened.register ?? 0, params);
  const cents = reading ? solfegeFoldCents(unfoldedCents(reading), register, params).cents : undefined;
  // The frame's own part inside its note's window is counted first, and only then does a window the
  // frame has reached the end of close on it
  const tally = tallyAfter(opened, timing, params, startMs, index, timeMs, deltaMs, reading, cents);
  const tallied = { ...opened, readingIndex: index, tally, register };
  const updated = closeWindows(tallied, timing, params, timeMs);
  if (cents === undefined || intoMs >= timing.lengthMs || !isSampleDue(opened, timeMs)) {
    return { ...state, score, take: updated };
  }
  const sample = {
    intoMs,
    midi: noteMidi(state, index) + cents / 100,
    cents,
    noteIndex: index,
    weak: reading?.weak === true,
  };
  return { ...state, score, take: { ...updated, trace: push(opened.trace, sample), sampledAtMs: timeMs } };
}

// When, on the take's scale, the count-in starts reading frames: the starting pitch's end (its one
// beat from the count-in's start) plus guideTailMs (see The count-in in the header). Negative: the
// count-in is before the take's start.
export function solfegeCountInReadFromMs(phrase: SolfegePhrase): number {
  return countInReadFromMs(timingOf(phrase, solfegeLevelParams[LearnLevel.Easy]));
}

function countInReadFromMs(timing: PhraseTiming): number {
  return timing.beatMs - timing.countInMs + learnListeningTunables.guideTailMs;
}

// A frame of the count-in, sung `intoMs` (< 0) before the first note. Once the starting pitch and its
// tail are over, the scorer reads it against the first note and it moves the count-in's register;
// nothing is tallied or drawn. Before that, and around each click (and in sing-along the guide's
// start), the meter alone.
function countInFrame(
  state: SolfegeSessionState,
  captured: CaptureFrame,
  intoMs: number,
  timing: PhraseTiming,
): SolfegeSessionState {
  const take = state.take;
  if (!take || intoMs < countInReadFromMs(timing) || hearsCountInSound(state, timing, intoMs)) {
    return { ...state, score: hearFrame(state.score, captured) };
  }
  const params = paramsOf(state);
  const { score } = scoreFrame(state.score, captured, noteMidi(state, 0), scoringParams(params));
  const countInRegister = registerAfter(score, captured.timeMs, take.countInRegister ?? 0, params);
  const updated = countInRegister === (take.countInRegister ?? 0) ? take : { ...take, countInRegister };
  return { ...state, score, take: updated };
}

// Whether a count-in frame sung `intoMs` before the first note may have heard one of the count-in's
// clicks, or in sing-along the guide beginning at the take's start (see solfegeClickMaskMs). There is
// a click on every beat of the bar before the take, and none at the take's start.
function hearsCountInSound(state: SolfegeSessionState, timing: PhraseTiming, intoMs: number): boolean {
  const sinceBarMs = intoMs + timing.countInMs; // from the count-in's first click
  const beat = Math.floor(sinceBarMs / timing.beatMs);
  if (sinceBarMs - beat * timing.beatMs < solfegeClickMaskMs) {
    return true; // just after a click
  }
  // just before the next sound: the next beat's click, or in sing-along the guide's first note
  const nextSounds = beat + 1 < currentEntry(state).phrase.beatsPerBar || state.guide === 'singAlong';
  return nextSounds && (beat + 1) * timing.beatMs - sinceBarMs <= solfegeClickLeadMs;
}

// The register after this frame, moved only by the voice as the frame read it: its own reading,
// before the scorer's smoothing, and one the scorer's continuity guards passed as read (or let go as
// a genuine leap). A smoothed reading can blend one the scorer folded an octave onto the note with
// one it did not, and a guarded one is drawn part of the way from the last reading toward this one;
// either can sit near the note unflagged in the middle of a move, and would put a man singing an
// octave down back in the note's own octave as he goes from one note to the next.
function registerAfter(score: NoteScore, timeMs: number, register: number, params: SolfegeLevelParams): number {
  const own = score.readingAtMs === timeMs ? score.frameReading : undefined;
  if (!own || (own.guard !== undefined && own.guard !== 'unfolded')) {
    return register;
  }
  return solfegeFoldCents(unfoldedCents(own), register, params).register;
}

function isSampleDue(take: SolfegeTake, timeMs: number): boolean {
  return take.sampledAtMs === undefined || timeMs - take.sampledAtMs >= solfegeTraceSampleMs;
}

// The open window's tally after this frame: the frame's span clipped to the window counts as on
// pitch or close when the frame was confidently read within those bands, and its cents join the
// median when the frame's own time is inside the window. The first frame past a window's end still
// counts the part of its span inside it; its reading, timed past the end, already belongs to what
// the singer did next, so it does not join the median or say the note was heard. A note whose window
// has already closed gathers nothing more.
function tallyAfter(
  take: SolfegeTake,
  timing: PhraseTiming,
  params: SolfegeLevelParams,
  startMs: number,
  index: number,
  timeMs: number,
  deltaMs: number,
  reading: PitchReading | undefined,
  cents: number | undefined,
): SolfegeTally | undefined {
  if (index < take.grades.length) {
    return take.tally;
  }
  const tally = take.tally?.index === index ? take.tally : { index, onMs: 0, nearMs: 0 };
  if (!reading || reading.weak || cents === undefined) {
    return tally;
  }
  const window = timing.windows[index];
  const overlapMs = Math.min(timeMs, startMs + window.endMs) - Math.max(timeMs - deltaMs, startMs + window.startMs);
  if (overlapMs <= 0) {
    return tally; // the settle at the window's start, or past its end
  }
  const distance = Math.abs(cents);
  return {
    index,
    onMs: tally.onMs + (distance <= params.toleranceCents ? overlapMs : 0),
    nearMs: tally.nearMs + (distance <= params.closeCents ? overlapMs : 0),
    deviations: timeMs <= startMs + window.endMs ? push(tally.deviations, cents) : tally.deviations,
  };
}

// ---- Stacks ----------------------------------------------------------------------------------

function push<T>(stack: SolfegeStack<T> | undefined, item: T): SolfegeStack<T> {
  return { top: item, rest: stack, size: (stack?.size ?? 0) + 1 };
}

// Oldest first
function stackToArray<T>(stack: SolfegeStack<T> | undefined): T[] {
  const items = new Array<T>(stack?.size ?? 0);
  for (let node = stack, index = items.length - 1; node; node = node.rest, index -= 1) {
    items[index] = node.top;
  }
  return items;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

// ---- Selectors: the session and the sound ------------------------------------------------------

// 0-based, the phrase the session is on (on the summary, the number of phrases)
export function solfegePhraseIndex(state: SolfegeSessionState): number {
  return state.finished.length;
}

export function solfegePhraseCount(state: SolfegeSessionState): number {
  return state.phrases.length;
}

// The phrase on screen, with its do; on the summary the last one
export function currentSolfegePhrase(state: SolfegeSessionState): SolfegeSessionPhrase {
  return currentEntry(state);
}

export function solfegeSoundRequest(state: SolfegeSessionState): SolfegeSoundRequest | undefined {
  return state.sound;
}

// What the page schedules for the sound asked for, from the time it starts it at
export function solfegeSoundPlan(state: SolfegeSessionState): SolfegeSoundPlan | undefined {
  return state.sound ? soundPlanFor(state, state.sound) : undefined;
}

function phraseSoundNotes(state: SolfegeSessionState, offsetMs: number, soft: boolean): SolfegeSoundNote[] {
  const { spans } = currentTiming(state);
  return spans.map((span, noteIndex) => ({
    midi: noteMidi(state, noteIndex),
    offsetMs: offsetMs + span.startMs,
    durationMs: span.endMs - span.startMs,
    soft,
    noteIndex,
  }));
}

function soundPlanFor(state: SolfegeSessionState, sound: SolfegeSoundRequest): SolfegeSoundPlan {
  const timing = currentTiming(state);
  const kind = sound.kind;
  switch (kind) {
    case 'phrase':
      return { kind, notes: phraseSoundNotes(state, 0, false), clickOffsetsMs: [], lengthMs: timing.lengthMs };
    case 'take': {
      const beats = currentEntry(state).phrase.beatsPerBar;
      const clickOffsetsMs = Array.from({ length: beats }, (_beat, beat) => beat * timing.beatMs);
      const startingPitch: SolfegeSoundNote = {
        midi: noteMidi(state, 0),
        offsetMs: 0,
        durationMs: timing.beatMs,
        soft: false,
      };
      if (state.guide !== 'singAlong') {
        return { kind, notes: [startingPitch], clickOffsetsMs, lengthMs: timing.countInMs };
      }
      const guide = phraseSoundNotes(state, timing.countInMs, true);
      return { kind, notes: [startingPitch, ...guide], clickOffsetsMs, lengthMs: timing.countInMs + timing.lengthMs };
    }
    case 'recording': {
      const lengthMs = sound.clip ? sound.clip.endMs - sound.clip.startMs : timing.takeEndMs;
      return { kind, notes: [], clickOffsetsMs: [], lengthMs };
    }
  }
}

// The sound has reported its start and the clock has not reached its end
export function isSoundPlaying(state: SolfegeSessionState): boolean {
  const endsMs = state.sound?.endsMs;
  return endsMs !== undefined && (state.clockMs ?? 0) < endsMs;
}

// The phrase is playing on the listen screen, or asked for and about to ("Playing the phrase")
export function isSolfegePhrasePlaying(state: SolfegeSessionState): boolean {
  const sound = state.sound;
  return state.phase === 'listen' && sound?.kind === 'phrase' && (sound.endsMs === undefined || isSoundPlaying(state));
}

// "Hear mine" is playing, or asked for: the button says "Stop"
export function isSolfegeHearingMine(state: SolfegeSessionState): boolean {
  const sound = state.sound;
  return (
    state.phase === 'result' && sound?.kind === 'recording' && (sound.endsMs === undefined || isSoundPlaying(state))
  );
}

// "Hear guide" is playing on the result, or asked for: the pill says "Stop"
export function isSolfegeHearingGuide(state: SolfegeSessionState): boolean {
  const sound = state.sound;
  return state.phase === 'result' && sound?.kind === 'phrase' && (sound.endsMs === undefined || isSoundPlaying(state));
}

// How far "Hear mine" or "Hear guide" has played into the take, on the take's own scale
// (solfegeTraceSamples' intoMs), for the playhead; undefined when neither is playing or has started.
// The phrase's sound starts where the take's first note does, so its time is the take's.
export function solfegePlaybackIntoMs(state: SolfegeSessionState): number | undefined {
  const sound = state.sound;
  if (!(isSolfegeHearingMine(state) || isSolfegeHearingGuide(state))) {
    return undefined;
  }
  if (sound?.atMs === undefined || sound.endsMs === undefined) {
    return undefined;
  }
  const playedMs = Math.min(Math.max(0, (state.clockMs ?? sound.atMs) - sound.atMs), sound.endsMs - sound.atMs);
  return (sound.clip?.startMs ?? 0) + playedMs; // a kept part of the take starts where the audio does
}

// The result's first solfegeResultGuardMs on the clock, while its buttons are not taken (see The
// result's first moments in the header)
export function isSolfegeResultSettling(state: SolfegeSessionState): boolean {
  const endedAtMs = state.take?.endedAtMs;
  return (
    state.phase === 'result' &&
    endedAtMs !== undefined &&
    (state.clockMs ?? endedAtMs) < endedAtMs + solfegeResultGuardMs
  );
}

// Whether the page must poll the audio clock: a sound playing, a count-in or take under way, or the
// result's guard still to lift
export function solfegeClockRunning(state: SolfegeSessionState): boolean {
  return (
    isSoundPlaying(state) ||
    (state.phase === 'sing' && state.take?.startMs !== undefined) ||
    isSolfegeResultSettling(state)
  );
}

// Where the page aims the known-target estimator: during the count-in the first note, then each
// note from aimDelayMs after its lagged start (see The estimator's aim in the header). Undefined
// when nothing is being sung.
export function estimatorTargetMidi(state: SolfegeSessionState): number | undefined {
  if (state.phase !== 'sing' || !state.take) {
    return undefined;
  }
  const startMs = state.take.startMs;
  if (startMs === undefined || state.clockMs === undefined) {
    return noteMidi(state, 0);
  }
  return noteMidi(state, readingIndexAt(currentTiming(state), paramsOf(state), startMs, state.clockMs));
}

// The note a frame at `timeMs` is read against during the take; undefined before the take starts
export function solfegeFrameNoteIndex(state: SolfegeSessionState, timeMs: number): number | undefined {
  const startMs = state.take?.startMs;
  if (startMs === undefined) {
    return undefined;
  }
  return readingIndexAt(currentTiming(state), paramsOf(state), startMs, timeMs);
}

// The take's span on the audio clock, from its first note to the end of its tail: what the page
// slices the recording with, and what "Hear mine" plays when the page kept all of it
export function solfegeTakeSpan(state: SolfegeSessionState): SolfegeSpan | undefined {
  const startMs = state.take?.startMs;
  if (startMs === undefined) {
    return undefined;
  }
  return { startMs, endMs: startMs + currentTiming(state).takeEndMs };
}

// ---- Selectors: the screens -------------------------------------------------------------------

export function solfegeMicLevel(state: SolfegeSessionState): number {
  return state.score.level;
}

// How many count-in beats are left, the one sounding included; undefined outside the count-in
export function solfegeCountInBeatsLeft(state: SolfegeSessionState): number | undefined {
  const take = state.take;
  if (state.phase !== 'sing' || !take) {
    return undefined;
  }
  const beats = currentEntry(state).phrase.beatsPerBar;
  if (take.startMs === undefined || state.clockMs === undefined) {
    return beats;
  }
  const leftMs = take.startMs - state.clockMs;
  if (leftMs <= 0) {
    return undefined;
  }
  // a hair under a whole beat is still that beat, whatever the floating point made of the bar
  return Math.min(beats, Math.ceil(leftMs / currentTiming(state).beatMs - 1e-6));
}

// The Ready · Set · Go! word pulsing now, and how far along its pulse, for the sing screen's cue row
// (see Ready · Set · Go! in the header): only in sing, once the take's start is known. "Ready" and
// "Set" fall on the count-in's beats two and one before the take, so a four-beat count-in shows no word
// on its first two beats and a three-beat one none on its first; "Go!" falls on the take's start
// and has faded within its first beat. Undefined between pulses, and in listen and the result.
export function solfegeCueNow(state: SolfegeSessionState): VisibleCue | undefined {
  const startMs = state.take?.startMs;
  if (state.phase !== 'sing' || startMs === undefined || state.clockMs === undefined) {
    return undefined;
  }
  const { beatMs } = currentTiming(state);
  return visibleCue(state.clockMs, { ready: startMs - 2 * beatMs, set: startMs - beatMs, go: startMs }, beatMs);
}

// ms into the take by the clock, while the phrase runs: where the dot is across the staff
export function solfegeTakeIntoMs(state: SolfegeSessionState): number | undefined {
  const startMs = state.take?.startMs;
  if (state.phase !== 'sing' || startMs === undefined || state.clockMs === undefined) {
    return undefined;
  }
  const intoMs = state.clockMs - startMs;
  return intoMs >= 0 && intoMs < currentTiming(state).lengthMs ? intoMs : undefined;
}

// The note the screen shows as current: the one sounding in listen, the one due by the clock in the
// take; undefined otherwise
export function solfegeCurrentNoteIndex(state: SolfegeSessionState): number | undefined {
  if (state.phase === 'listen') {
    const sound = state.sound;
    if (sound?.kind !== 'phrase' || sound.atMs === undefined || state.clockMs === undefined) {
      return undefined;
    }
    const intoMs = state.clockMs - sound.atMs;
    const { spans } = currentTiming(state);
    const index = spans.findIndex((span) => intoMs >= span.startMs && intoMs < span.endMs);
    return index >= 0 ? index : undefined;
  }
  const intoMs = solfegeTakeIntoMs(state);
  return intoMs === undefined ? undefined : noteIndexAt(currentTiming(state).spans, intoMs);
}

export type SolfegeNoteDisplay = 'upcoming' | 'current' | 'played' | 'passing' | ResultColor;

// One state per note. Listen: against the phrase's sound - upcoming, current as it sounds, played
// once past; all played when nothing is playing (idle), all upcoming while it is asked for. The
// take: upcoming, current by the clock, then played until its window closes and its colour shows;
// a passing note is 'passing' once past. The result: every note's colour, passing notes passing.
export function solfegeNoteDisplays(state: SolfegeSessionState): SolfegeNoteDisplay[] {
  const { spans, graded } = currentTiming(state);
  if (state.phase === 'listen') {
    return spans.map((span) => listenDisplay(state, span));
  }
  const take = state.take;
  if (!take || take.startMs === undefined) {
    return spans.map(() => 'upcoming');
  }
  const intoMs = (state.clockMs ?? -Infinity) - take.startMs;
  return spans.map((span, index): SolfegeNoteDisplay => {
    if (index < take.grades.length || state.phase === 'result' || intoMs >= span.endMs) {
      if (!graded[index]) {
        return 'passing';
      }
      return take.grades[index]?.color ?? 'played';
    }
    return intoMs >= span.startMs ? 'current' : 'upcoming';
  });
}

function listenDisplay(state: SolfegeSessionState, span: SolfegeSpan): SolfegeNoteDisplay {
  const sound = state.sound;
  if (sound?.kind !== 'phrase') {
    return 'played';
  }
  if (sound.atMs === undefined || state.clockMs === undefined) {
    return 'upcoming';
  }
  const intoMs = state.clockMs - sound.atMs;
  if (intoMs >= span.endMs) {
    return 'played';
  }
  return intoMs >= span.startMs ? 'current' : 'upcoming';
}

// The live dot during the take: x by the clock (intoMs, on phraseNoteSpans' scale) and the current
// note; the singer's pitch, its cents from the note it was read against and the scorer's dot colour
// when a voice is being read (the scorer keeps a reading through a dropout up to matchGapMs); with
// no voice, `voiced` false and the dot rides the current note's lane. In the count-in, once frames
// are read (see The count-in in the header), a voice gives the dot at the start of the first note
// (intoMs 0) at the singer's pitch; no voice gives no dot.
export interface SolfegeLiveDot {
  intoMs: number;
  noteIndex: number;
  voiced: boolean;
  midi?: number;
  cents?: number;
  color?: 'green' | 'yellow';
}

export function solfegeLiveDot(state: SolfegeSessionState): SolfegeLiveDot | undefined {
  const intoMs = solfegeTakeIntoMs(state);
  if (intoMs === undefined) {
    return countInDot(state);
  }
  const noteIndex = noteIndexAt(currentTiming(state).spans, intoMs);
  const reading = foldedReading(state);
  if (!reading) {
    return { intoMs, noteIndex, voiced: false };
  }
  return voicedDot(state, intoMs, noteIndex, reading);
}

// The count-in's dot: only with a voice, at the start of the first note
function countInDot(state: SolfegeSessionState): SolfegeLiveDot | undefined {
  if (state.take?.startMs === undefined || solfegeCountInBeatsLeft(state) === undefined) {
    return undefined;
  }
  const reading = foldedReading(state);
  return reading ? voicedDot(state, 0, 0, reading) : undefined;
}

function voicedDot(
  state: SolfegeSessionState,
  intoMs: number,
  noteIndex: number,
  reading: PitchReading,
): SolfegeLiveDot {
  return {
    intoMs,
    noteIndex,
    voiced: true,
    midi: noteMidi(state, state.take?.readingIndex ?? 0) + reading.cents / 100,
    cents: reading.cents,
    color: readingDot(reading, false)?.color,
  };
}

// The scorer's latest reading with the cents folded as grading folds them - into the register the
// take has established, which that reading itself set or followed - its tolerance asked again. The
// score is fresh from each count-in, and only the count-in's read frames (against the first note)
// and the take's frames (against take.readingIndex) score, so a reading is always one of those: a
// count-in reading (no take frame read yet) is folded by the count-in's register, a take's by the
// take's.
function foldedReading(state: SolfegeSessionState): PitchReading | undefined {
  const reading = state.score.reading;
  const take = state.take;
  if (!reading || !take) {
    return undefined;
  }
  const params = paramsOf(state);
  const register = take.readingIndex === undefined ? take.countInRegister : take.register;
  const { cents } = solfegeFoldCents(unfoldedCents(reading), register ?? 0, params);
  return { ...reading, cents, inTolerance: Math.abs(cents) <= params.toleranceCents };
}

export type SolfegeHint = 'higher' | 'lower';

export const solfegeHigherText = 'a little higher';
export const solfegeLowerText = 'a little lower';

// The live hint with the voiced dot - during the take, and in the count-in once it reads frames -
// from the latest reading: undefined on pitch, with no voice, and outside the phrase. In tempo there
// is no "Hold it!": the phrase moves on whatever the singer does.
export function solfegeHint(state: SolfegeSessionState): SolfegeHint | undefined {
  const reading = foldedReading(state);
  if (!reading || !solfegeLiveDot(state)?.voiced) {
    return undefined;
  }
  const hint = readingHint(reading);
  return hint === 'higher' || hint === 'lower' ? hint : undefined;
}

export function solfegeHintText(hint: SolfegeHint): string {
  return hint === 'higher' ? solfegeHigherText : solfegeLowerText;
}

// The singer's line so far, oldest first; flattened once per take state (frames add to it, renders
// read it)
const flattenedTraces = new WeakMap<SolfegeStack<SolfegeTraceSample>, SolfegeTraceSample[]>();

export function solfegeTraceSamples(state: SolfegeSessionState): SolfegeTraceSample[] {
  const trace = state.take?.trace;
  if (!trace) {
    return [];
  }
  const known = flattenedTraces.get(trace);
  if (known) {
    return known;
  }
  const samples = stackToArray(trace);
  flattenedTraces.set(trace, samples);
  return samples;
}

// What the listen screen says about home, as text runs with the syllables to set in bold: "do is
// the home note, the bedrock the whole song rests on. This phrase starts on sol, below do."
export interface SolfegeTextPart {
  text: string;
  strong?: boolean;
}

export function solfegeHomeLine(phrase: SolfegePhrase): SolfegeTextPart[] {
  const { syllable, position } = phraseStart(phrase);
  const opening: SolfegeTextPart[] = [
    { text: 'do', strong: true },
    { text: ' is the home note, the bedrock the whole song rests on. This phrase starts on ' },
  ];
  if (position === 'on') {
    return [...opening, { text: 'do', strong: true }, { text: '.' }];
  }
  if (syllable === 'do') {
    // do in another octave (Joy to the World starts on the do above)
    return [
      ...opening,
      { text: position === 'above' ? 'the high ' : 'the low ' },
      { text: 'do', strong: true },
      { text: '.' },
    ];
  }
  return [...opening, { text: syllable, strong: true }, { text: `, ${position} do.` }];
}

// ---- Selectors: the result and the summary -------------------------------------------------------

// The take's colour per note, undefined for a passing note or a window not yet closed
export function solfegeResultColors(state: SolfegeSessionState): SolfegeNoteColors {
  const { spans } = currentTiming(state);
  const grades = state.take?.grades ?? [];
  return spans.map((_span, index) => grades[index]?.color);
}

// Each note's grade, for the sentence and anyone who wants the shares
export function solfegeResultGrades(state: SolfegeSessionState): (SolfegeNoteGrade | undefined)[] {
  const { spans } = currentTiming(state);
  const grades = state.take?.grades ?? [];
  return spans.map((_span, index) => grades[index]);
}

// "8 of 9 notes"
export function solfegeResultCountText(state: SolfegeSessionState): string {
  return solfegeNoteCountText(solfegeResultColors(state));
}

export const solfegeAllGreenText = 'Every note on pitch.';
export const solfegeNothingHeardText = 'We did not hear you. Sing out and try again.';

const ordinals = [
  'first',
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
  'eleventh',
  'twelfth',
];

function ordinal(nth: number): string {
  return ordinals[nth - 1] ?? `${nth}th`;
}

// The result screen's one sentence, about the miss most worth fixing (see the plan's "The
// sentence"). It names syllables, up or down and step or jump, never an interval, a key or a letter.
// It is built as text runs with the syllables it names marked strong, the shape solfegeHomeLine has,
// so the screen sets them in bold without looking for them in the text: "do" is an English word too,
// and no sentence can bold the wrong one when the parts say which runs are syllables (#3722).
export function solfegeResultSentenceParts(state: SolfegeSessionState): SolfegeTextPart[] {
  return solfegeSentenceParts(currentEntry(state).phrase, solfegeResultGrades(state), paramsOf(state));
}

// The same sentence as plain text, for tests and anything that wants a string
export function solfegeResultSentence(state: SolfegeSessionState): string {
  return partsText(solfegeResultSentenceParts(state));
}

export function solfegeSentence(
  phrase: SolfegePhrase,
  grades: (SolfegeNoteGrade | undefined)[],
  params: SolfegeLevelParams = solfegeLevelParams[LearnLevel.Easy],
): string {
  return partsText(solfegeSentenceParts(phrase, grades, params));
}

export function solfegeSentenceParts(
  phrase: SolfegePhrase,
  grades: (SolfegeNoteGrade | undefined)[],
  params: SolfegeLevelParams = solfegeLevelParams[LearnLevel.Easy],
): SolfegeTextPart[] {
  const graded = grades.filter((grade): grade is SolfegeNoteGrade => grade !== undefined);
  if (graded.length > 0 && graded.every((grade) => !grade.heard)) {
    return [plain(solfegeNothingHeardText)];
  }
  const index = worstNoteIndex(grades);
  if (index === undefined) {
    return [plain(solfegeAllGreenText)];
  }
  return joinParts([...howItWent(phrase, index, grades[index]!, params), plain(' '), ...whatToTry(phrase, index)]);
}

function plain(text: string): SolfegeTextPart {
  return { text };
}

function syllablePart(syllable: SolfegeSyllable): SolfegeTextPart {
  return { text: syllable, strong: true };
}

function partsText(parts: SolfegeTextPart[]): string {
  return parts.map((part) => part.text).join('');
}

// Neighbouring plain runs merged into one, so the parts are as few as the bold syllables allow
function joinParts(parts: SolfegeTextPart[]): SolfegeTextPart[] {
  const joined: SolfegeTextPart[] = [];
  for (const part of parts) {
    const last = joined[joined.length - 1];
    if (last && !last.strong && !part.strong) {
      joined[joined.length - 1] = plain(last.text + part.text);
    } else {
      joined.push(part);
    }
  }
  return joined;
}

// A red note before a yellow one; among equals the one furthest off by its median (a note not heard
// at all furthest of all); the earlier on a tie
function worstNoteIndex(grades: (SolfegeNoteGrade | undefined)[]): number | undefined {
  let worst: { index: number; rank: number; distance: number } | undefined;
  grades.forEach((grade, index) => {
    if (!grade || grade.color === 'green') {
      return;
    }
    const rank = grade.color === 'red' ? 2 : 1;
    const distance = grade.medianCents === undefined ? Infinity : Math.abs(grade.medianCents);
    if (!worst || rank > worst.rank || (rank === worst.rank && distance > worst.distance)) {
      worst = { index, rank, distance };
    }
  });
  return worst?.index;
}

// "do" when the syllable comes once in the phrase, "second do" otherwise; the syllable is its own part
function noteName(phrase: SolfegePhrase, index: number): SolfegeTextPart[] {
  const syllables = phraseSyllables(phrase);
  const syllable = syllables[index];
  if (syllables.filter((each) => each === syllable).length === 1) {
    return [syllablePart(syllable)];
  }
  const nth = syllables.slice(0, index + 1).filter((each) => each === syllable).length;
  return [plain(`${ordinal(nth)} `), syllablePart(syllable)];
}

// The first half: how the note went. A note whose median sits within the tolerance was graded down
// for how little of it was sung on pitch rather than for where it was, which neither "flat" nor
// "sharp" describes, so it is said plainly (a case the plan's rules leave open).
function howItWent(
  phrase: SolfegePhrase,
  index: number,
  grade: SolfegeNoteGrade,
  params: SolfegeLevelParams,
): SolfegeTextPart[] {
  const name = noteName(phrase, index);
  if (!grade.heard || grade.medianCents === undefined) {
    return [plain('We did not hear the '), ...name, plain('.')];
  }
  if (Math.abs(grade.medianCents) <= params.toleranceCents) {
    return [plain('We heard only part of the '), ...name, plain('.')];
  }
  const direction = grade.medianCents < 0 ? 'flat' : 'sharp';
  const how = grade.color === 'yellow' ? ` was a little ${direction}.` : ` was ${direction}.`;
  return [plain('The '), ...name, plain(how)];
}

// The second half: what to try. The first note is about the starting pitch; a repeated note about
// holding it; anything else about the move into it from the note before. Whether two notes are the
// same, and whether a move goes up or down, is by pitch, so fa to fi is a move up; a move is a step
// when it goes to the next degree or stays on its own (fa to fi), a jump otherwise.
function whatToTry(phrase: SolfegePhrase, index: number): SolfegeTextPart[] {
  if (index === 0) {
    return [plain('Listen for the first note before you start.')];
  }
  const semitones = phraseSemitones(phrase);
  const syllables = phraseSyllables(phrase);
  const to = semitones[index];
  const from = semitones[index - 1];
  const syllable = syllables[index];
  if (from === to) {
    let run = 1;
    while (index - run >= 0 && semitones[index - run] === to) {
      run += 1;
    }
    return [plain('Hold '), syllablePart(syllable), plain(` steady the ${ordinal(run)} time.`)];
  }
  const degrees = Math.abs(phrase.notes[index].degree - phrase.notes[index - 1].degree);
  const move = degrees <= 1 ? 'step' : 'jump';
  return [
    plain(`Try the ${move} from `),
    syllablePart(syllables[index - 1]),
    plain(` ${to > from ? 'up' : 'down'} to `),
    syllablePart(syllable),
    plain(' again.'),
  ];
}

// The summary, over the last take of each phrase sung
export function solfegeSummaryScore(state: SolfegeSessionState): number {
  return solfegeSessionScore(state.finished);
}

export function solfegeSummarySquares(state: SolfegeSessionState): ResultColor[] {
  return state.finished.map(solfegePhraseColor);
}

export function solfegeBestStreak(state: SolfegeSessionState): number {
  return solfegeLongestGreenRun(state.finished);
}
